"use client";

/**
 * ImageModal — click-to-expand image viewer.
 *
 * Renders a thumbnail that, when clicked, opens a full-screen modal overlay
 * with a shared layout animation powered by Framer Motion.
 *
 * Uses Next.js `<Image>` for automatic WebP/AVIF conversion, responsive
 * sizing, and built-in lazy loading on the thumbnail.
 */

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";

/** Wrap Next.js Image with framer-motion for layout animations. */
const MotionImage = motion.create(Image);

export default function ImageModal({
  src = "https://s3.amazonaws.com/images.seroundtable.com/google-links-1510059186.jpg",
  className,
  wrapperClassName,
  imageClassName,
  alt,
}: {
  src?: string;
  className?: string;
  wrapperClassName?: string;
  imageClassName?: string;
  alt?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <>
      <div
        className={cn("inline-block transition-colors duration-500", wrapperClassName || className)}
      >
        <MotionImage
          onClick={() => setIsOpen(true)}
          layoutId={`image-${src}`}
          className={cn(
            "h-full w-full object-cover transition-transform duration-300",
            imageClassName || className,
          )}
          src={src}
          alt={alt || "Project image"}
          width={800}
          height={600}
          sizes="(max-width: 768px) 100vw, 50vw"
          quality={85}
          loading="lazy"
        />
      </div>

      {mounted &&
        createPortal(
          <AnimatePresence mode="wait">
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
                animate={{ opacity: 1, backdropFilter: "blur(16px)" }}
                exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
                transition={{ duration: 0.25 }}
                className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-4 sm:p-8"
                onClick={() => setIsOpen(false)}
              >
                {/* Close Button */}
                <motion.button
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 z-[10000] p-2.5 bg-black/60 hover:bg-black/90 border border-white/10 rounded-full text-white transition-all hover:scale-105"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(false);
                  }}
                  aria-label="Close modal"
                >
                  <X className="h-6 w-6" />
                </motion.button>

                <motion.div
                  layoutId={`image-${src}`}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30,
                  }}
                  className="relative h-[85vh] w-[92vw] max-w-6xl flex items-center justify-center"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Modal image uses fill + object-contain so long mobile images fit perfectly without being cut off */}
                  <Image
                    src={src}
                    alt={alt || "Project image"}
                    fill
                    className="object-contain rounded-xl drop-shadow-2xl"
                    sizes="95vw"
                    quality={95}
                    priority
                  />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </>
  );
}
